import React, { useState } from "react";
import { File, FileType } from "../../types/api/get-file-with-siblings-success-response";
import { getImageUrl } from "../../../../utils/image-helper/get-image-url";
import './file-card.css';
import { Popover } from "antd";
import { FileListType } from "../file-list/file-list";
import { CheckOutlined, CloseOutlined, QuestionCircleTwoTone } from "@ant-design/icons";

type FileCardProps = {
    file: File;
    cardType: FileListType;
    onDelete?: (id: number) => void;
    onFolderOpen?: (id: number) => void;
    onSelect?: (id: number) => void;
    onFolderRename?: (name: string) => void;
    onFileMove?: () => void;
};

export const FileCard = ({ file, onDelete, onFolderOpen, cardType, onSelect, onFolderRename, onFileMove }: FileCardProps) => {
    const [isFolderNameEditable, setIsFolderNameEditable] = useState(false);
    const [isMenuPopoverOpen, setIsMenuPopoverOpen] = useState(false);

    const handleDeleteClick = () => {
        if (onDelete) onDelete(file.id);
        setIsMenuPopoverOpen(false);
    };

    const handleOpenFolderClick = () => {
        if (file.type === FileType.Directory && onFolderOpen) onFolderOpen(file.id);
        setIsMenuPopoverOpen(false);
    }

    const handleChooseFileClick = () => {
        if (onSelect) onSelect(file.id);
        setIsMenuPopoverOpen(false);
    }

    const handleFolderRenameClick = () => {
        if (file.type === FileType.Directory) setIsFolderNameEditable(true);
        setIsMenuPopoverOpen(false);
    };

    const handleFolderRename = (name: string) => {
        if (file.type === FileType.Directory && onFolderRename) onFolderRename(name);
        setIsFolderNameEditable(false);
    };

    const handleFileMoveClick = () => {
        if ([FileType.SimpleFile, FileType.ModelFile].includes(file.type) && onFileMove) onFileMove();
        setIsMenuPopoverOpen(false);
    };

    return (
        <div className='file-card box-shadow-4-4-8-0 border-radius-18'>
            <div
                className={`d-flex align-items-center ${ !file.isFile && 'cursor-pointer' }`}
                onDoubleClick={file.type === FileType.Directory ? () => { if (onFolderOpen) onFolderOpen(file.id); } : undefined}
            >
                <img src={file.type === FileType.Directory ? getImageUrl('folder-icon.png') : getImageUrl('file-icon.png')} alt={file.type === FileType.Directory ? 'folder-icon' : 'file-icon'} />
                &nbsp;&nbsp;
                <span style={{ width: '180px' }}>
                    <FolderNameHandler
                        originalFolderName={file.name}
                        editable={isFolderNameEditable}
                        onCancel={() => setIsFolderNameEditable(false)}
                        onSave={handleFolderRename}
                    />
                </span>
            </div>
            <Popover
                content={
                    <FileMenu
                        file={file}
                        type={cardType}
                        onDelete={handleDeleteClick}
                        onOpenFolder={handleOpenFolderClick}
                        onChooseFile={handleChooseFileClick}
                        onFolderRename={handleFolderRenameClick}
                        onFileMove={handleFileMoveClick}
                    />
                }
                open={isMenuPopoverOpen}
                onOpenChange={(open) => setIsMenuPopoverOpen(open)}
                trigger="click"
            >
                <img className="cursor-pointer" src={getImageUrl('file-menu-icon.png')} alt="menu-icon" />
            </Popover>
        </div>
    );
};

type FileMenuProps = {
    file: File;
    type: FileListType;
    onChooseFile?: () => void;
    onOpenFolder?: () => void;
    onDelete?: () => void;
    onFolderRename?: () => void;
    onFileMove?: () => void;
};

export const FileMenu = ({ file, type, onChooseFile, onOpenFolder, onDelete, onFolderRename, onFileMove }: FileMenuProps) => {
    const handleChooseFileClick: React.MouseEventHandler<HTMLLIElement> = (e) => {
        e.stopPropagation();
        if (onChooseFile) onChooseFile();
    };

    const handleOpenFolderClick: React.MouseEventHandler<HTMLLIElement> = (e) => {
        e.stopPropagation();
        if (onOpenFolder) onOpenFolder();
    };

    const handleDeleteClick: React.MouseEventHandler<HTMLLIElement> = (e) => {
        e.stopPropagation();
        if (onDelete) onDelete();
    };

    const handleDownloadClick: React.MouseEventHandler<HTMLLIElement> = (e) => {
        e.stopPropagation();
        if ((file.type === FileType.SimpleFile || file.type === FileType.ModelFile) && file.url) window.open(file.url, '_blank'); 
    };

    const handleRenameClick: React.MouseEventHandler<HTMLLIElement> = (e) => {
        e.stopPropagation();
        if (file.type === FileType.Directory && onFolderRename) onFolderRename();
    };

    const handleFileMoveClick: React.MouseEventHandler<HTMLLIElement> = (e) => {
        e.stopPropagation();
        if ((file.type === FileType.ModelFile || file.type === FileType.SimpleFile) && onFileMove) onFileMove();
    };

    if (type === 'fileChooser') {
        if (file.type === FileType.ModelFile || file.type === FileType.SimpleFile) {
            return (
                <ul className="file-card-popover-menu">
                    <li onClick={handleChooseFileClick}>Kiválasztás</li>
                </ul>
            );
        }
    
        return (
            <ul className="file-card-popover-menu">
                <li onClick={handleOpenFolderClick}>Megnyitás</li>
            </ul>
        );
    }
    
    if (file.type === FileType.SimpleFile) {
        return (
            <ul className="file-card-popover-menu">
                <li onClick={handleFileMoveClick}>Áthelyezés</li>
                <li onClick={handleDeleteClick}>Törlés</li>
                <li onClick={handleDownloadClick}>Letöltés</li>
            </ul>
        );
    }
    
    if (file.type === FileType.ModelFile) {
        return (
            <ul className="file-card-popover-menu">
                <li onClick={handleFileMoveClick}>Áthelyezés</li>
                <li className="disabled">
                    Törlés
                    <QuestionCircleTwoTone
                        title='Modelltéren keresztül feltöltött fájl nem törölhető, csak a modelltéren keresztül.'
                        style={{ fontSize: '15px', verticalAlign: 'text-bottom', paddingLeft: '3px', cursor: 'default' }}
                    />
                </li>
                <li onClick={handleDownloadClick}>Letöltés</li>
            </ul>
        );
    }

    return (
        <ul className="file-card-popover-menu">
            <li onClick={handleDeleteClick}>Törlés</li>
            <li onClick={handleRenameClick}>Átnevezés</li>
            <li onClick={handleOpenFolderClick}>Megnyitás</li>
        </ul>
    );
};

type FolderNameHandlerProps = {
    originalFolderName: string;
    editable: boolean;
    onSave: (name: string) => void;
    onCancel: () => void;
};

function FolderNameHandler({ originalFolderName, editable, onSave, onCancel }: FolderNameHandlerProps) {
    const [value, setValue] = useState(originalFolderName);

    if (editable) {
        return (
            <div className='position-relative' onClick={(e) => e.stopPropagation()}>
                <input
                    autoFocus
                    style={{ width: '100%', paddingRight: '30px' }}
                    type='text'
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    maxLength={255}
                />
                <CheckOutlined
                    title='Mentés'
                    style={{
                        color: 'green',
                        position: 'absolute',
                        right: '20px',
                        top: '5px'
                    }}
                    onClick={
                        () => {
                            if (value) onSave(value);
                            else {
                                setValue(originalFolderName);
                                onCancel();
                            }
                        }
                    }
                />
                <CloseOutlined
                    title='Elvetés'
                    style={{
                        color: 'red',
                        position: 'absolute',
                        right: '3px',
                        top: '5px'
                    }}
                    onClick={
                        () => {
                            setValue(originalFolderName);
                            onCancel();
                        }
                    }
                />
            </div>
        );
    }

    return <span className="text-force-wrap" >{originalFolderName}</span>;
}